import { graphql } from 'gql.tada';
import { client } from '../client';
import { pdxEvent } from '../../utils/noflake';

export const getRetailerSettingsQuery = graphql(`
  query RetailerSettings {
    retailerSettings {
      translation
      contextTranslations
      styles {
        font {
          fontFamily
          fontWeight
          src
        }
        theme {
          ctaColor
          primaryColor
          secondaryColor
          textColor
        }
        themeVarOverrides
      }
      features {
        orderTokens
        isGiftLoginEnabled
        isOrderless
        itemAttributes {
          displayValueOnly
          displayKeyValue
        }
        itemSelectConfig {
          item_properties {
            property
            enabled
          }
          item_order {
            order_by
            direction
          }
        }
        widgets {
          requestDetailsBottom {
            type
            variant
            href
            target
            styleOverride
            size
          }
        }
        assets {
          loginBackgroundImageDesktop
          loginBackgroundImageMobile
          favicon
        }
        headerHtml {
          BASE
          DESKTOP
          MOBILE
        }
        header {
          logo {
            src
            href
            alt
          }
          navigationLinks {
            text
            url
          }
          headerLayout
          headerLogoAlignment
          headerNavAlignment
          headerBackgroundColor
          headerTextColor
          headerFixed
        }
      }
      workflow {
        id
        tasks {
          id
          guards
          mode
          scope
          conditions {
            all {
              field
              operator
              value
            }
            any {
              field
              operator
              value
            }
          }
        }
      }
      promos {
        ids
        content
        promoId
      }
      methodLayout
    }
  }
`);

export const getRetailerSettingsQueryFn = async () => {
  pdxEvent({
    event_category: 'get_data',
    event_type: 'get_retailer_settings',
    event_value: 'retailer_settings',
  });

  try {
    const result = await client.request(getRetailerSettingsQuery);

    pdxEvent({
      event_category: 'get_data',
      event_type: 'get_retailer_settings_success',
      event_value: 'retailer_settings',
    });

    return result;
  } catch (error) {
    pdxEvent({
      event_category: 'error',
      event_type: 'get_retailer_settings_error',
      event_value: 'get_data failed to get retailer settings',
    });
    throw error;
  }
};

export type GetRetailerSettingsResult = Awaited<
  ReturnType<typeof getRetailerSettingsQueryFn>
>;
export type RetailerSettings = NonNullable<
  GetRetailerSettingsResult['retailerSettings']
>;
export type Features = NonNullable<RetailerSettings['features']>;
export type Styles = NonNullable<RetailerSettings['styles']>;
export type Font = Styles['font'];
export type OrderTokenType = Features['orderTokens'][number];
export type Widget = NonNullable<
  NonNullable<Features['widgets']>[keyof NonNullable<Features['widgets']>]
>[number];

export type PromoConfig = {
  ids: string[];
  content: string;
};

export type ItemSelectConfig = NonNullable<Features['itemSelectConfig']>;
export type ItemPropertyConfig = ItemSelectConfig['item_properties'][number];
export type ItemOrderConfig = ItemSelectConfig['item_order'];
export type ItemSelectProperty = ItemPropertyConfig['property'];
export type SortDirection = ItemOrderConfig['direction'];
